import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { officeApi } from '../../../api/candidate-office.api';
import { http } from '../../../api/http';
import { schoolApi } from '../../../api/school.api';
import { useAuthStore } from '../../../store/auth.store';
import type { CandidateDetail, UpdateCandidatePayload } from '../../../types';

/** Réponse de GET /v1/exam-operations/readiness/:id (champs utilisés par la fiche). */
export interface ExamReadiness {
  calculatedAt: string;
  administrative: { status: string; blockers: { label: string; source: string }[] };
  humanDecision: { eligible: boolean; label: string };
  reservation: { constraints: { label: string; source: string }[]; days: string[]; centers: string[] };
}

/** Champs de GET /v1/candidate-receivables/:id utilisés par l'Aperçu (fil d'inscription : « Ensuite », 23/10/2026). */
export interface ReceivablesSummary {
  /** Montant en retard : échéancier s'il existe (même à revoir), sinon factures échues non réglées. */
  overdueCents: number;
  /** Échéancier à revoir (ex. facture annulée) : le retard reste affiché, avec une alerte. */
  needsReview?: boolean;
  /** Relances suspendues par une rétractation à rembourser (T17) : l'élève n'est pas « en retard » (accueil, planning). */
  refundHold?: boolean;
  /** Motif qui suspend les relances (celui d'une rétractation à rembourser en tête), sinon null. */
  blockedReason?: string | null;
  /** Échéance la plus ancienne en retard (échéancier ou facture). */
  oldestOverdueOn?: string | null;
  /** Jour de l'école selon le serveur (AAAA-MM-JJ). */
  today?: string;
  version?: number;
  dueCents?: number;
  fundingPendingCents?: number;
  history?: { version: number; created_at: string }[];
  reminders?: { id: string; level: number; state: string; created_at: string }[];
  lines: { dueOn: string; overdue?: boolean; amountCents?: number; paidCents?: number; outstandingCents?: number; payer?: string }[];
}

/**
 * Données complémentaires de l'Aperçu : suivi administratif (même clé de cache que
 * l'onglet Dossier), historique, réservation automatique. Ces routes sont réservées
 * aux rôles owner / manager / agent : les autres rôles voient un état « indisponible ».
 */
export function useOverviewData(id: string) {
  const role = useAuthStore((s) => s.user?.role);
  const scope = useAuthStore((s) => `${s.user?.id}:${s.user?.role}:${s.activeSchoolId}`);
  const allowed = ['owner', 'manager', 'agent'].includes(role ?? '');

  // Chaque réponse est vérifiée au chargement : une forme inattendue devient une erreur
  // (« indisponible ») au lieu de faire planter la fiche. La vérification a lieu dans `queryFn`
  // (pas dans `select`) car certaines clés sont partagées avec les onglets Dossier et Finance :
  // une réponse invalide ne doit jamais entrer dans le cache commun.
  const office = useQuery({
    queryKey: ['candidate-office', scope, id],
    queryFn: () => fetchOffice(id),
    enabled: allowed,
    retry: 1,
  });

  const history = useQuery({
    queryKey: ['office-history', scope, id, 1],
    queryFn: () => officeApi.history(id, 1).then((data) => expectShape(data, Array.isArray(data?.items))),
    enabled: allowed,
    retry: 1,
  });

  const examReadiness = useQuery({
    queryKey: ['exam-readiness', id],
    queryFn: () =>
      http
        .get<ExamReadiness>(`/v1/exam-operations/readiness/${id}`)
        .then((r) =>
          expectShape(r.data, Array.isArray(r.data?.administrative?.blockers) && Array.isArray(r.data?.reservation?.constraints)),
        ),
    enabled: allowed,
    retry: 1,
  });

  // Échéancier : même clé de cache que le bloc « Échéancier et relances » (onglet Finance).
  const receivablesScope = useAuthStore((s) => `${s.user?.id}:${s.activeSchoolId}:${s.user?.role}`);
  const receivablesAllowed = ['owner', 'manager', 'agent', 'super_admin'].includes(role ?? '');
  const receivables = useQuery({
    queryKey: ['candidate-finance', id, 'receivables', receivablesScope],
    queryFn: () =>
      http.get<ReceivablesSummary>(`/v1/candidate-receivables/${id}`).then((r) => expectShape(r.data, Array.isArray(r.data?.lines))),
    enabled: receivablesAllowed && !!id,
    retry: 1,
  });

  return { allowed, office, history, examReadiness, receivables };
}

/** Rejette une réponse dont la forme n'est pas celle attendue (l'écran affiche alors « indisponible »). */
function expectShape<T>(data: T, valid: boolean): T {
  if (!valid) throw new Error('Réponse inattendue du serveur.');
  return data;
}

/** Suivi administratif vérifié à la lecture : même lecture pour l'Aperçu et l'onglet Dossier (cache commun). */
export const fetchOffice = (id: string) =>
  officeApi.get(id).then((data) => expectShape(data, Array.isArray(data?.dossier?.blockers) && Array.isArray(data?.dossier?.items) && !!data?.office));

/** Rôles autorisés à modifier la fiche (mêmes rôles que PATCH /v1/candidates/:id côté serveur). */
export const CANDIDATE_EDIT_ROLES = ['owner', 'manager', 'agent', 'super_admin'];

/** `true` si l'utilisateur peut modifier la fiche ; le rôle lecture seule ne voit pas les actions d'écriture. */
export function useCanEditCandidate() {
  const role = useAuthStore((s) => s.user?.role);
  return CANDIDATE_EDIT_ROLES.includes(role ?? '');
}

/**
 * Modification ponctuelle de la fiche (centres, disponibilités…) avec la version
 * courante (verrouillage optimiste). La version est lue dans le cache au moment de
 * l'envoi, puis mise à jour avec celle renvoyée par le serveur : deux modifications
 * rapides à la suite ne se heurtent donc pas à un conflit de version.
 */
export function useCandidatePatch(detail: CandidateDetail) {
  const queryClient = useQueryClient();
  const key = ['candidate-detail', detail.id];
  return useMutation({
    mutationFn: (payload: UpdateCandidatePayload) => {
      const cached = queryClient.getQueryData<CandidateDetail>(key);
      return schoolApi.updateCandidate(detail.id, { ...payload, version: cached?.version ?? detail.version });
    },
    onSuccess: (saved: Partial<CandidateDetail> | undefined, payload) => {
      queryClient.setQueryData<CandidateDetail>(key, (old) =>
        old ? { ...old, ...(payload as Partial<CandidateDetail>), version: saved?.version ?? old.version } : old,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: key });
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      queryClient.invalidateQueries({ queryKey: ['exam-readiness', detail.id] });
      // Date du code : le score de préparation et l'alerte d'expiration en dépendent.
      queryClient.invalidateQueries({ queryKey: ['readiness-score', detail.id] });
    },
  });
}
